export type Author = {
  name: string;
  avatar: string;
};

export type ArticleCard = {
  category?: string;
  time: string;
  title: string;
  excerpt?: string;
  img: string;
  author?: Author;
};

export type VideoCard = {
  category: string;
  time: string;
  title: string;
  duration: string;
  img: string;
  subscriberOnly?: boolean;
};

export type SidebarItem = {
  time: string;
  title: string;
  img: string;
  author?: Author;
};

/**
 * The masthead sections.
 *
 * Two of these pointed somewhere else entirely: 醫生專欄 opened 醫生會客室 and
 * 醫健一分鐘 opened a single video detail page, because neither had a listing of
 * its own. Both are categories now. 糖胖與你 moved the other way — it is a TAG,
 * not a category, so it points at the tag listing.
 */
export const NAV_ITEMS: { label: string; href: string; hasChevron?: boolean }[] = [
  { label: "醫健新知", href: "/ej-health/medical-news" },
  { label: "醫生會客室", href: "/ej-health/doctors-lounge" },
  { label: "醫學通識", href: "/ej-health/medical-knowledge" },
  { label: "健康精選", href: "/ej-health/featured" },
  { label: "醫生專欄", href: "/ej-health/doctor-columns" },
  { label: "醫健一分鐘", href: "/ej-health/one-minute" },
  { label: "食出健康", href: "/ej-health/eat-well" },
  { label: "醫 Tech", href: "/ej-health/med-tech" },
  { label: "糖胖與你", href: `/ej-health/tag/${encodeURIComponent("糖胖與你")}` },
  // { label: "更多", hasChevron: true, href: "/ej-health" },
];

// Sub-menu surfaced from the "更多" nav item's dropdown.
export const MORE_NAV_ITEMS = [
  { label: "動出健康", href: "/ej-health/exercise" },
  { label: "健康財富", href: "/ej-health/health-wealth" },
  { label: "運動懶人包", href: "/ej-health/workout-guide" },
];

const AVATAR_1 = "/images/ej-health/avatar-doctor-1.png";
const AVATAR_2 = "/images/ej-health/avatar-doctor-2.png";
const AVATAR_3 = "/images/ej-health/avatar-doctor-3.png";
const AVATAR_4 = "/images/ej-health/avatar-doctor-4.png";
const AVATAR_5 = "/images/ej-health/avatar-doctor-5.png";

// ── Hero Section ─────────────────────────────────────────
export const HERO_FEATURE: ArticleCard = {
  category: "醫健新知",
  time: "12分鐘前",
  title: "【木瓜】一文看清營養價值、好處、卡路里、食譜及食用禁忌",
  excerpt:
    "木瓜（Papaya）是熱帶地區最常見的水果之一，在香港街市和超市全年都能輕鬆買到。木瓜卡路里高唔高？木瓜真的能豐胸嗎？生木瓜同熟木瓜有什麼分別？本文由營養角度出發，深入剖析木瓜營養價值、6大好處、破解常見迷思，並推介3款簡易食譜及食用禁忌，助你食得更健康、更放心。",
  img: "/images/ej-health/figma/hero-feature-papaya.png",
};

export const HERO_SECONDARY: ArticleCard[] = [
  {
    category: "健康精選",
    time: "12分鐘前",
    title: "內地網購「毒衣」風波！致癌物超標 27 倍　央視揭平價衫暗藏殺機",
    img: "/images/ej-health/figma/hero-secondary-toxic-clothing.png",
  },
  {
    category: "醫健新知",
    time: "24分鐘前",
    title: "【五指毛桃】一文看清營養價值、好處、禁忌及食譜",
    img: "/images/ej-health/figma/hero-secondary-fivefingerherb.png",
  },
  {
    category: "醫學通識",
    time: "57分鐘前",
    title: "中藥如何治療肺結節",
    img: "/images/ej-health/figma/hero-secondary-tcm-lung.png",
    author: { name: "梁浩榮中醫師", avatar: AVATAR_1 },
  },
];

export const HERO_SIDEBAR: SidebarItem[] = [
  {
    time: "15分鐘前",
    title: "脖子淋巴腫大疑雲（上）",
    img: "/images/ej-health/figma/hero-sidebar-neck-lymph.png",
    author: { name: "王建芳醫生", avatar: AVATAR_2 },
  },
  {
    time: "28分鐘前",
    title: "浮針源自傳統直針刺",
    img: "/images/ej-health/figma/hero-sidebar-acupuncture.png",
    author: { name: "袁康就博士", avatar: AVATAR_3 },
  },
  {
    time: "58分鐘前",
    title: "資助18至49歲長期病患 預防併發症 速打流感針",
    img: "/images/ej-health/figma/hero-sidebar-flu-vaccine.png",
    author: { name: "陳俊平醫生", avatar: AVATAR_4 },
  },
  {
    time: "45分鐘前",
    title: "世界肥胖日｜台灣醫生推「2分鐘行樓梯法」內臟脂肪激減13%",
    img: "/images/ej-health/figma/hero-sidebar-stairs.png",
    author: { name: "葉佩珮醫生", avatar: AVATAR_5 },
  },
  {
    time: "45分鐘前",
    title: "應對心房顫動危機",
    img: "/images/ej-health/figma/hero-sidebar-heart.png",
    author: { name: "陳立城醫生", avatar: AVATAR_1 },
  },
  // Sixth row: 熱門專欄 renders the whole list and the card asked for six.
  {
    time: "1小時前",
    title: "冬季濕疹反覆發作 保濕與外用藥如何配合",
    img: "/images/ej-health/thumb-broccoli.png",
    author: { name: "梁浩榮中醫師", avatar: AVATAR_1 },
  },
];

// ── Section: 人氣熱話 (Popular Topics) ────────────────────
export const POPULAR_LIST: SidebarItem[] = [
  {
    time: "15分鐘前",
    title: "脖子淋巴腫大疑雲（上）",
    img: "/images/ej-health/figma/popular-neck-lymph.png",
    author: { name: "王建芳醫生", avatar: AVATAR_2 },
  },
  {
    time: "28分鐘前",
    title: "浮針源自傳統直針刺",
    img: "/images/ej-health/figma/popular-acupuncture.png",
    author: { name: "袁康就博士", avatar: AVATAR_3 },
  },
  {
    time: "45分鐘前",
    title: "【大蒜】一文看清營養價值、食用好處、卡路里、食譜及禁忌",
    img: "/images/ej-health/figma/popular-garlic.png",
  },
  {
    time: "58分鐘前",
    title: "資助18至49歲長期病患 預防併發症 速打流感針",
    img: "/images/ej-health/figma/popular-flu-vaccine.png",
    author: { name: "陳俊平醫生", avatar: AVATAR_4 },
  },
  {
    time: "28分鐘前",
    title: "消委會去頭皮洗頭水｜4款洗頭水樣本含有歐盟禁用成份「吡硫鎓鋅」",
    img: "/images/ej-health/figma/popular-shampoo.png",
  },
  // Sixth row: 最新文章 renders six.
  {
    time: "1小時前",
    title: "流感高峰期未過 醫生教你分辨感冒與流感徵狀",
    img: "/images/ej-health/thumb-flu-vaccine.png",
    author: { name: "王建芳醫生", avatar: AVATAR_2 },
  },
];

// ── Section: 醫健新知 (Latest News) ──────────────────────
export const LATEST_NEWS_HERO: ArticleCard = {
  time: "12分鐘前",
  title: "世界肥胖日｜台灣醫生推「2分鐘行樓梯法」內臟脂肪激減13%",
  img: "/images/ej-health/figma/latest-hero-stairs-method.png",
};

export const LATEST_NEWS_LIST: SidebarItem[] = [
  {
    time: "15分鐘前",
    title: "【木瓜】一文看清營養價值、好處、卡路里、食譜及食用禁忌",
    img: "/images/ej-health/figma/latest-thumb-papaya.png",
  },
  {
    time: "28分鐘前",
    title: "【五指毛桃】一文看清營養價值、好處、禁忌及食譜",
    img: "/images/ej-health/figma/latest-thumb-fivefingerherb.png",
  },
  {
    time: "45分鐘前",
    title: "【大蒜】一文看清營養價值、食用好處、卡路里、食譜及禁忌",
    img: "/images/ej-health/figma/latest-thumb-garlic.png",
  },
  {
    time: "58分鐘前",
    title: "【五指毛桃】一文看清營養價值、好處、禁忌及食譜",
    img: "/images/ej-health/figma/latest-thumb-fivefingerherb-2.png",
  },
];

// ── Section: 醫學通識 (Medical Knowledge) ────────────────
export const MEDICAL_HERO: ArticleCard = {
  time: "12分鐘前",
  title: "依據診斷審核傷病援助上訴  揭醫療評估委員會面紗",
  img: "/images/ej-health/figma/knowledge-hero-medical-eval.png",
  author: { name: "傅錦峯醫生", avatar: AVATAR_1 },
};

export const MEDICAL_LIST: SidebarItem[] = [
  {
    time: "15分鐘前",
    title: "【木瓜】一文看清營養價值、好處、卡路里、食譜及食用禁忌",
    img: "/images/ej-health/figma/knowledge-thumb-papaya.png",
    author: { name: "葉佩珮醫生", avatar: AVATAR_5 },
  },
  {
    time: "28分鐘前",
    title: "大壓力易患視網膜病變",
    img: "/images/ej-health/figma/knowledge-thumb-retina.png",
    author: { name: "鄧愷悠醫生", avatar: AVATAR_3 },
  },
  {
    time: "45分鐘前",
    title: "世界肥胖日｜台灣醫生推「2分鐘行樓梯法」內臟脂肪激減13%",
    img: "/images/ej-health/figma/knowledge-thumb-stairs.png",
    author: { name: "葉佩珮醫生", avatar: AVATAR_5 },
  },
  {
    time: "58分鐘前",
    title: "【五指毛桃】一文看清營養價值、好處、禁忌及食譜",
    img: "/images/ej-health/figma/knowledge-thumb-fivefingerherb.png",
    author: { name: "王建芳醫生", avatar: AVATAR_2 },
  },
];

// ── Section: 動出健康 (Exercise Videos) ──────────────────
export const EXERCISE_VIDEOS: VideoCard[] = [
  {
    category: "動出健康",
    time: "12分鐘前",
    title: "伸展解決五常痛7｜伸正腰背減疼痛",
    duration: "08:24",
    img: "/images/ej-health/figma/exercise-stretch-7.png",
  },
  {
    category: "動出健康",
    time: "1天前",
    title: "伸展解決五常痛6｜蓆上活動拉鬆肩膊",
    duration: "08:24",
    img: "/images/ej-health/figma/exercise-stretch-6.png",
  },
  {
    category: "動出健康",
    time: "3天前",
    title: "伸展解決五常痛5｜Pat Pat都要輕輕鬆鬆",
    duration: "08:24",
    img: "/images/ej-health/figma/exercise-stretch-5.png",
    subscriberOnly: true,
  },
];

/* ── Section: 相關主題 (Related Topics tags) ──────────────
   These were EJ Tech's tags — 創科KOL, FinTech, 電競, Startup本周摘要 — sitting
   on a health home page, and none of them had a listing behind it. They are the
   site's own topics now, so every pill in the cloud opens the topic it names.
   Keep this list in step with `fixtures/ej-health/category/fixtures.ts`. */
export const RELATED_TAGS = [
  "營養學",
  "醫學通識",
  "食出健康",
  "醫 Tech",
  "糖胖與你",
  "動出健康",
  "健康財富",
];

// ── Section: 健康精選 (Health Featured) ──────────────────
export const FEATURED_HERO: ArticleCard = {
  time: "12分鐘前",
  title: "糖尿病合併壞膽固醇雙重夾擊 組合式藥物治療成控醇護心關鍵",
  img: "/images/ej-health/figma/featured-hero-diabetes.png",
};

export const FEATURED_LIST: SidebarItem[] = [
  {
    time: "15分鐘前",
    title: "內地網購「毒衣」風波！致癌物超標 27 倍　央視揭平價衫暗藏殺機",
    img: "/images/ej-health/figma/featured-thumb-toxic-clothing.png",
  },
  {
    time: "28分鐘前",
    title: "消委會去頭皮洗頭水｜4款洗頭水樣本含有歐盟禁用成份「吡硫鎓鋅」",
    img: "/images/ej-health/figma/featured-thumb-shampoo.png",
  },
  {
    time: "45分鐘前",
    title: "灰甲初期｜3大灰甲初期症狀　灰甲高危人士、治療、預防方法一文看清",
    img: "/images/ej-health/figma/featured-thumb-nail.png",
  },
  {
    time: "58分鐘前",
    title: "資助18至49歲長期病患 預防併發症 速打流感針",
    img: "/images/ej-health/figma/featured-thumb-flu-vaccine.png",
    author: { name: "陳俊平醫生", avatar: AVATAR_4 },
  },
];

// ── Section: 健康財富 (Health Wealth) ────────────────────
export const WEALTH_HERO: ArticleCard = {
  time: "12分鐘前",
  title: "大壓力易患視網膜病變",
  img: "/images/ej-health/figma/wealth-hero-retina.png",
  author: { name: "葉佩珮醫生", avatar: AVATAR_5 },
};

export const WEALTH_LIST: SidebarItem[] = [
  {
    time: "15分鐘前",
    title: "脖子淋巴腫大疑雲（上）",
    img: "/images/ej-health/figma/wealth-thumb-neck-lymph.png",
    author: { name: "王建芳醫生", avatar: AVATAR_2 },
  },
  {
    time: "28分鐘前",
    title: "術後曲膝更自然  局部膝關節置換復原迅速",
    img: "/images/ej-health/figma/wealth-thumb-knee.png",
    author: { name: "鄧偉文醫生", avatar: AVATAR_1 },
  },
  {
    time: "45分鐘前",
    title: "應對心房顫動危機",
    img: "/images/ej-health/figma/wealth-thumb-heart.png",
    author: { name: "陳立城醫生", avatar: AVATAR_4 },
  },
  {
    time: "58分鐘前",
    title: "脂肪肝恐誘發大腸癌",
    img: "/images/ej-health/figma/eat-card-liver.png",
    author: { name: "施蘊知醫生", avatar: AVATAR_3 },
  },
];

// ── Section: 醫生會客室 (Doctors' Lounge Videos) ─────────
export const DOCTORS_VIDEOS: VideoCard[] = [
  {
    category: "醫生會客室",
    time: "12分鐘前",
    title: "打工仔壓力太大應否辭職？",
    duration: "08:24",
    img: "/images/ej-health/figma/lounge-work-stress.png",
  },
  {
    category: "醫生會客室",
    time: "1天前",
    title: "如何分辨同事是否有情緒問題？",
    duration: "08:24",
    img: "/images/ej-health/figma/lounge-mood-recognize.png",
  },
  {
    category: "醫生會客室",
    time: "3天前",
    title: "哪些工種特別容易出情緒病？",
    duration: "08:24",
    img: "/images/ej-health/figma/lounge-mood-disorder.png",
    subscriberOnly: true,
  },
];

// ── Section: 醫生專欄 (Doctor Columns) ───────────────────
export const COLUMNS_HERO: ArticleCard = {
  time: "12分鐘前",
  title: "依據診斷審核傷病援助上訴  揭醫療評估委員會面紗",
  img: "/images/ej-health/figma/columns-hero-medical-eval.png",
  author: { name: "傅錦峯醫生", avatar: AVATAR_1 },
};

export const COLUMNS_LIST: SidebarItem[] = [
  {
    time: "15分鐘前",
    title: "大壓力易患視網膜病變",
    img: "/images/ej-health/figma/columns-thumb-retina.png",
    author: { name: "葉佩珮醫生", avatar: AVATAR_5 },
  },
  {
    time: "28分鐘前",
    title: "眼白增生遮視線",
    img: "/images/ej-health/figma/columns-thumb-eye.png",
    author: { name: "鄧愷悠醫生", avatar: AVATAR_3 },
  },
  {
    time: "45分鐘前",
    title: "脖子淋巴腫大疑雲（上）",
    img: "/images/ej-health/figma/columns-thumb-neck-lymph.png",
    author: { name: "王建芳醫生", avatar: AVATAR_2 },
  },
  {
    time: "58分鐘前",
    title: "術後曲膝更自然  局部膝關節置換復原迅速",
    img: "/images/ej-health/figma/columns-thumb-knee.png",
    author: { name: "鄧偉文醫生", avatar: AVATAR_1 },
  },
];

// ── Section: 醫健一分鐘 (One Minute Health) ──────────────
export const ONE_MIN_HERO: ArticleCard = {
  time: "12分鐘前",
  title: "三角步 增加核心及下肢力量",
  img: "/images/ej-health/figma/onemin-hero-triangle-step.png",
  author: { name: "王彥", avatar: AVATAR_3 },
};

export const ONE_MIN_LIST: SidebarItem[] = [
  {
    time: "15分鐘前",
    title: "少吃多餐 碳水蛋白質助保暖  長者冬天飲食注意",
    img: "/images/ej-health/figma/onemin-thumb-nutrition.png",
    author: { name: "潘偉剛博士", avatar: AVATAR_2 },
  },
  {
    time: "28分鐘前",
    title: "浮針源自傳統直針刺",
    img: "/images/ej-health/figma/onemin-thumb-acupuncture.png",
    author: { name: "袁康就博士", avatar: AVATAR_3 },
  },
  {
    time: "45分鐘前",
    title: "乾冬注意香港腳",
    img: "/images/ej-health/figma/onemin-thumb-athletes-foot.png",
    author: { name: "陳厚毅醫生", avatar: AVATAR_5 },
  },
  {
    time: "1小時前",
    title: "手腳冰冷未必是體虛 一分鐘看清成因",
    img: "/images/ej-health/thumb-acupuncture.png",
    author: { name: "王建芳醫生", avatar: AVATAR_2 },
  },
];

// ── Section: 食出健康 (Eat Healthy) ──────────────────────
export const EAT_HEALTHY: SidebarItem[] = [
  {
    time: "12分鐘前",
    title: "大壓力易患視網膜病變",
    img: "/images/ej-health/figma/eat-card-retina.png",
    author: { name: "葉佩珮醫生", avatar: AVATAR_5 },
  },
  {
    time: "24分鐘前",
    title: "脖子淋巴腫大疑雲（上）",
    img: "/images/ej-health/figma/eat-card-neck-lymph.png",
    author: { name: "王建芳醫生", avatar: AVATAR_2 },
  },
  {
    time: "57分鐘前",
    title: "脂肪肝恐誘發大腸癌",
    img: "/images/ej-health/figma/eat-card-liver.png",
    author: { name: "施蘊知醫生", avatar: AVATAR_3 },
  },
];

// ── Section: 醫 Tech ─────────────────────────────────────
export const TECH_LIST: SidebarItem[] = [
  {
    time: "12分鐘前",
    title: "結合物聯網與人工智能 「安心貼」保障病人安全",
    img: "/images/ej-health/figma/tech-card-iot.png",
    author: { name: "雷俊達醫生", avatar: AVATAR_4 },
  },
  {
    time: "24分鐘前",
    title: "抗生素與大腸健康（三）",
    img: "/images/ej-health/figma/tech-card-antibiotics.png",
    author: { name: "施蘊知醫生", avatar: AVATAR_3 },
  },
  {
    time: "57分鐘前",
    title: "抗體藥物複合體新進展",
    img: "/images/ej-health/figma/tech-card-antibody.png",
    author: { name: "李兆康醫生", avatar: AVATAR_1 },
  },
];

// ── Section: 運動懶人包 (Exercise Pack) ──────────────────
export const EXERCISE_PACK: SidebarItem[] = [
  {
    time: "12分鐘前",
    title: "元宵佳節漫談馬年 「赤馬紅羊」收火養心",
    img: "/images/ej-health/figma/pack-card-horse.png",
    author: { name: "陳仲謀醫生", avatar: AVATAR_4 },
  },
  {
    time: "24分鐘前",
    title: "一招「皇祖坐殿」 告別關節痛",
    img: "/images/ej-health/figma/pack-card-throne.png",
    author: { name: "王彥", avatar: AVATAR_3 },
  },
  {
    time: "57分鐘前",
    title: "下潛頂膝 提升身體防禦力",
    img: "/images/ej-health/figma/pack-card-dive-knee.png",
    author: { name: "王彥", avatar: AVATAR_3 },
  },
];

// ── Footer Links ─────────────────────────────────────────
/**
 * The footer columns.
 *
 * They were the MAIN site's — 即時 / 投資 / 財經 / 地產, EJFQ, 即市排名 — none of
 * which is 信健康's subject, and each a bare string resolved through the shared
 * route table, so a reader in this vertical was offered the parent paper's
 * sections and nothing of the site they were on. Every entry now names a real
 * EJ Health surface and carries its route, the same treatment EJ Tech's footer
 * had ("update the foot items to related to ej TECH").
 *
 * Keep in step with `NAV_ITEMS` above and the category registry in
 * `fixtures/ej-health/category/fixtures.ts`.
 */
export const EJHEALTH_FOOTER_COLUMNS: {
  title: string;
  items: { label: string; href: string }[];
}[] = [
  {
    // Every article category, in the masthead's order. 醫生專欄, 醫健一分鐘,
    // 健康財富 and 運動懶人包 used to sit under 專欄 and 影片; they are
    // categories like the rest, and splitting them across three headings made
    // the footer read as three different taxonomies.
    title: "分類",
    items: [
      { label: "醫健新知", href: "/ej-health/medical-news" },
      { label: "醫學通識", href: "/ej-health/medical-knowledge" },
      { label: "健康精選", href: "/ej-health/featured" },
      { label: "醫生專欄", href: "/ej-health/doctor-columns" },
      { label: "醫健一分鐘", href: "/ej-health/one-minute" },
      { label: "食出健康", href: "/ej-health/eat-well" },
      { label: "醫 Tech", href: "/ej-health/med-tech" },
      { label: "健康財富", href: "/ej-health/health-wealth" },
      { label: "運動懶人包", href: "/ej-health/workout-guide" },
    ],
  },
  {
    // The two VIDEO categories, and only those. 醫健影片 pointed at a single
    // video detail page rather than a listing, so it is gone.
    title: "影片",
    items: [
      { label: "醫生會客室", href: "/ej-health/doctors-lounge" },
      { label: "動出健康", href: "/ej-health/exercise" },
    ],
  },
  {
    title: "熱門標籤",
    items: [
      { label: "糖胖與你", href: `/ej-health/tag/${encodeURIComponent("糖胖與你")}` },
      { label: "營養學", href: `/ej-health/tag/${encodeURIComponent("營養學")}` },
      { label: "食物安全", href: `/ej-health/tag/${encodeURIComponent("食物安全")}` },
      { label: "醫療科技", href: `/ej-health/tag/${encodeURIComponent("醫療科技")}` },
    ],
  },
  {
    title: "信報品牌",
    items: [
      { label: "信報財經新聞", href: "/" },
      { label: "信報財經月刊", href: "/ej-monthly" },
      { label: "EJ TECH", href: "/ej-tech" },
      { label: "信報教育", href: "/ej-education" },
      { label: "優雅生活", href: "/lj" },
      { label: "Ejinsight", href: "/ejinsight" },
    ],
  },
  {
    title: "關於",
    items: [
      { label: "搜尋", href: "/ej-health/search" },
      { label: "訂閱", href: "/subscription" },
      { label: "關於信報", href: "/about" },
      { label: "聯絡信報", href: "/contact" },
    ],
  },
];

export const FOOTER_LEGAL_LINKS = ["私隱政策", "服務協議", "免責聲明"];
